// Issue file upload utility — handles storage + document record + RAG trigger
import { supabase } from '@/lib/supabase'

/**
 * Upload document files from issue recording to customer_profile_documents
 * and trigger the RAG embedding pipeline.
 */
export async function uploadIssueFiles(
  files: File[],
  customerId: string | null,
  organizationId: string,
  issueId?: string | null,
  uploadedBy?: string | null,
): Promise<string[]> {
  if (!organizationId) {
    console.warn('No organizationId — skipping document upload')
    return []
  }

  // Get current user if not provided
  let userId = uploadedBy
  if (!userId) {
    const { data: { user } } = await supabase.auth.getUser()
    userId = user?.id ?? null
  }

  const documentIds: string[] = []

  for (const file of files) {
    const targetCustomer = customerId || 'unassigned'
    const storagePath = `customers/${targetCustomer}/${Date.now()}_${file.name}`

    const { error: uploadErr } = await supabase.storage
      .from('customer-documents')
      .upload(storagePath, file)

    if (uploadErr) {
      console.error('Document upload failed:', uploadErr)
      continue
    }

    // Build insert payload — only include issue_id if provided
    const insertPayload: Record<string, unknown> = {
      organization_id: organizationId,
      customer_id: customerId || null,
      uploaded_by: userId || null,
      file_name: file.name,
      file_type: file.type || 'application/octet-stream',
      file_size_bytes: file.size,
      storage_path: storagePath,
      storage_bucket: 'customer-documents',
      title: file.name.replace(/\.[^.]+$/, ''),
      category: 'other',
      processing_status: 'pending',
    }
    if (issueId) insertPayload.issue_id = issueId

    const { data: docData, error: insertErr } = await supabase
      .from('customer_profile_documents')
      .insert(insertPayload as never)
      .select('id')
      .single()

    if (insertErr) {
      console.error('Failed to create document record:', insertErr.message, insertErr.details)
    }

    if (docData) {
      documentIds.push(docData.id as string)
      await triggerProcessing(storagePath, targetCustomer, organizationId)
    } else {
      console.error('Failed to create document record for:', file.name)
    }
  }

  console.log(`Uploaded ${documentIds.length} documents to customer documents`)
  return documentIds
}

async function triggerProcessing(
  storagePath: string,
  customerId: string,
  organizationId: string,
): Promise<void> {
  try {
    await supabase.functions.invoke('process-document', {
      body: { filePath: storagePath, customerId, organizationId },
    })
  } catch (err) {
    console.warn('Document processing trigger failed:', err)
  }
}
